import api from 'services/api';
import { REJECT, array, num, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /connections` — see ../../README.md. Self-contained on
// purpose: its own type, its own schema, shared with nothing, including `searchConnections`,
// which asks a different question of the same route (one page of options for a picker) and
// would stop projecting the same fields the moment either changed.
//
// It reads the three fields that identify a connection. `object()` ignores the rest of the
// payload (parameters, workers, folder path, the list page's header config), so nothing else
// is parsed and nothing else can warn.
//
// NOTE: deliberately NOT scoped by `connection_type`. The one consumer mirrors the backend's
// REST dispatch, whose id lookup and name fallback both search every connection regardless of
// type. Filtering here would make that mirror resolve differently from the runtime.
//
// `GET /connections` defaults to 10 rows and enforces no maximum on `limit`, so one large page
// covers any installation we expect. The loop is the safety net, not the normal path.
const PAGE_SIZE = 2000;

/** A connection by its identifying fields. */
export interface ConnectionIdentity {
  readonly connection_id: number;
  readonly connection_name: string;
  readonly connection_type: string;
}

const identitySchema = object<ConnectionIdentity>({
  connection_id: num(REJECT),
  connection_name: str(),
  connection_type: str(),
});

interface ConnectionsPage {
  readonly connections: readonly ConnectionIdentity[];
  readonly pagination: { readonly total_records: number };
}

const responseSchema = object<ConnectionsPage>({
  connections: array(identitySchema),
  // Parsed to a number server-side, so a string here is drift.
  pagination: object<{ readonly total_records: number }>({ total_records: num(REJECT) }),
});

/**
 * Every connection, of every type, following pagination to exhaustion, in `connection_id` order.
 *
 * Refuses a degraded page. A lookup over a partial list does not come back "not found"; it can
 * resolve a reference to the wrong connection, e.g. fall back to a name match because the row
 * whose id matched was on the part that could not be read. Something called `getAll` that quietly
 * returns some has broken its promise, whoever is asking.
 *
 * Ordered by `connection_id` because offset paging needs a total order: the route's default,
 * `created_at`, can tie, and a tie lets a row slip between two pages.
 */
export const getAllConnections = async (): Promise<readonly ConnectionIdentity[]> => {
  const rows: ConnectionIdentity[] = [];
  const seen = new Set<number>();
  let fetched = 0;
  let total = 0;

  do {
    const res = await api.get('/connections', { params: { limit: PAGE_SIZE, offset: fetched, sortColumn: 'connection_id', sortOrder: 'ASC' } });
    const page = refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'getAllConnections' }));

    // An empty page means the server has no more to give, whatever its total claims — stop
    // rather than spin. Every other path advances `fetched`, so the loop always terminates.
    if (page.connections.length === 0) {
      break;
    }

    fetched += page.connections.length;
    total = page.pagination.total_records;

    // Offset paging over a table that can gain rows mid-sequence may repeat a row across pages;
    // the id keeps the result a set.
    for (const connection of page.connections) {
      if (!seen.has(connection.connection_id)) {
        seen.add(connection.connection_id);
        rows.push(connection);
      }
    }
  } while (fetched < total);

  return rows;
};
